// components/create/BasicSection.js
import React, { useState } from 'react';
import '../../styles/BasicSection.scss';
import Icon from '../Icon';
import MusicSection from './MusicSection';
import EventTypeSelector from './EventTypeSelector';
import PollsSection from './PollsSection';
import CustomFieldModal from './CustomFieldModal';
import { DateSheet, TimeSheet, DateTimeField } from './DateTimeSheet';
import { deviceTimeZone, timeZoneOptions } from '../../shared/eventTime';
import { emojiForEventType } from '../../constants/eventTypes';

const MAX_DESCRIPTION_LENGTH = 1040;
const MAX_TITLE_LENGTH = 40;


export default function BasicSection({
  title,
  setTitle,
  eventType,
  setEventType,
  description,
  setDescription,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  eventTimezone,
  setEventTimezone,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  location,
  onLocationPress,
  allowGuestUploads,
  setAllowGuestUploads,
  allowGuestComments,
  setAllowGuestComments,
  customFields,
  setCustomFields,
  polls,
  setPolls,
  playlist,
  setPlaylist,
  youtubePlaylist,
  setYoutubePlaylist,
  selectedMusicService,
  setSelectedMusicService,
  errors = {},
}) {
  const [showEventTypeDropdown, setShowEventTypeDropdown] = useState(false);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  // Which date/time sheet is open: 'startDate' | 'startTime' | 'endDate' |
  // 'endTime' | null. One piece of state rather than four booleans, because
  // only one can be open at a time and four booleans can disagree.
  const [openPicker, setOpenPicker] = useState(null);
  const [currentCustomField, setCurrentCustomField] = useState(null);

  const formatDate = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (time) => {
    if (!time) return 'Select time';
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleAddCustomField = () => {
    setCurrentCustomField(null);
    setShowCustomFieldModal(true);
  };

  const handleEditCustomField = (field) => {
    setCurrentCustomField(field);
    setShowCustomFieldModal(true);
  };

  const handleSaveCustomField = (field) => {
    const index = customFields.findIndex((f) => f.id === field.id);
    if (index !== -1) {
      const newFields = [...customFields];
      newFields[index] = field;
      setCustomFields(newFields);
    } else {
      setCustomFields([...customFields, field]);
    }
    setShowCustomFieldModal(false);
  };

  const handleDeleteCustomField = (id) => {
    setCustomFields(customFields.filter((field) => field.id !== id));
  };

  return (
    <div className="basic-section">
      {/* Title */}
      <div className="section-card">
        <label className="section-title">Invitation Title *</label>
        <input
          type="text"
          className={`text-input ${errors.title ? 'error' : ''}`}
          placeholder="Enter invitation title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={MAX_TITLE_LENGTH}
        />
        <div className="char-count">
          {title.length}/{MAX_TITLE_LENGTH}
        </div>
        {errors.title && <p className="error-text">{errors.title}</p>}
      </div>

      {/* Event Type */}
      <div className="section-card">
        <label className="section-title">Event Type *</label>
        {/* A sheet, not a dropdown.
            The dropdown opened behind the cards below it, and no z-index
            fixes that: every .section-card has a backdrop-filter, which
            promotes it to its own composited layer, and a later sibling on
            its own layer paints over an earlier sibling's positioned child
            regardless. See EventTypeSelector.js. */}
        <button
          type="button"
          className={`select-button ${errors.eventType ? 'error' : ''}`}
          onClick={() => setShowEventTypeDropdown(true)}
        >
          <span className="select-emoji" aria-hidden="true">
            {eventType ? emojiForEventType(eventType) : null}
          </span>
          <span>{eventType || 'Select event type'}</span>
          <Icon name="chevron-forward" />
        </button>

        {showEventTypeDropdown && (
          <EventTypeSelector
            value={eventType}
            onSelect={setEventType}
            onClose={() => setShowEventTypeDropdown(false)}
          />
        )}
        {errors.eventType && <p className="error-text">{errors.eventType}</p>}
      </div>

      {/* Date & Time */}
      <div className="section-card">
        <label className="section-title">Date & Time *</label>
        
        {/* Four buttons that open sheets, where four react-datepicker
            widgets used to drop light-themed panels into a dark form. */}
        <label className="field-label">Start</label>
        <div className="datetime-row">
          <DateTimeField
            label="Start date"
            value={startDate}
            placeholder="Select date"
            invalid={!!errors.startDate}
            format={formatDate}
            onOpen={() => setOpenPicker('startDate')}
          />
          <DateTimeField
            label="Start time"
            value={startTime}
            placeholder="Select time"
            invalid={!!errors.startTime}
            format={formatTime}
            onOpen={() => setOpenPicker('startTime')}
          />
        </div>

        <label className="field-label">End (Optional)</label>
        <div className="datetime-row">
          <DateTimeField
            label="End date"
            value={endDate}
            placeholder="Same day"
            format={formatDate}
            onOpen={() => setOpenPicker('endDate')}
          />
          <DateTimeField
            label="End time"
            value={endTime}
            placeholder="Select time"
            format={formatTime}
            onOpen={() => setOpenPicker('endTime')}
          />
        </div>

        {/* Which clock the times above are on: the host's own unless they
            pick another. Guests elsewhere see it beside the time, with their
            own one tap away (shared/eventTime.js). */}
        {setEventTimezone && (
          <>
            <label className="field-label" htmlFor="event-timezone">Time zone</label>
            <select
              id="event-timezone"
              className="timezone-select"
              value={eventTimezone || ''}
              onChange={(e) => setEventTimezone(e.target.value)}
            >
              {timeZoneOptions(deviceTimeZone()).map((o) => (
                <option key={o.zone} value={o.zone}>
                  {`${o.label} · ${o.city}${o.zone === deviceTimeZone() ? ' (your time zone)' : ''}`}
                </option>
              ))}
              {eventTimezone && !timeZoneOptions(deviceTimeZone()).some((o) => o.zone === eventTimezone) && (
                <option value={eventTimezone}>{eventTimezone}</option>
              )}
            </select>
          </>
        )}

        {openPicker === 'startDate' && (
          <DateSheet
            title="Start date"
            value={startDate}
            onChange={setStartDate}
            onClose={() => setOpenPicker(null)}
            minDate={new Date()}
          />
        )}
        {openPicker === 'startTime' && (
          <TimeSheet
            title="Start time"
            value={startTime}
            onChange={setStartTime}
            onClose={() => setOpenPicker(null)}
          />
        )}
        {openPicker === 'endDate' && (
          <DateSheet
            title="End date"
            value={endDate}
            onChange={setEndDate}
            onClose={() => setOpenPicker(null)}
            // An invitation cannot end before it starts.
            minDate={startDate || new Date()}
          />
        )}
        {openPicker === 'endTime' && (
          <TimeSheet
            title="End time"
            value={endTime}
            onChange={setEndTime}
            onClose={() => setOpenPicker(null)}
          />
        )}
        
        {errors.startDateTime && <p className="error-text">{errors.startDateTime}</p>}
      </div>

      {/* Location */}
      <div className="section-card">
        <label className="section-title">Location</label>
        <button
          type="button"
          className={`select-button ${errors.location ? 'error' : ''}`}
          onClick={onLocationPress}
        >
          <Icon name="location-outline" />
          <span>{location?.display_name || 'Venue TBD'}</span>
          <Icon name="search" />
        </button>
        
        {location && (
          <div className="selected-location">
            <div className="location-details">
              <p className="location-name">{location.display_name}</p>
              <p className="location-address">{location.formatted_address}</p>
            </div>
          </div>
        )}
        {errors.location && <p className="error-text">{errors.location}</p>}
      </div>

      {/* Description */}
      <div className="section-card">
        <label className="section-title">Details</label>
        <textarea
          className="textarea-input"
          placeholder="Share more details about your event..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={MAX_DESCRIPTION_LENGTH}
          rows={6}
        />
        <div className="char-count">
          {description.length}/{MAX_DESCRIPTION_LENGTH}
        </div>
      </div>

      {/* Photo Pool */}
      <div className="section-card">
        <label className="section-title">Photo Pool</label>
        <div className="toggle-row">
          <div className="toggle-info">
            <p className="toggle-label">Allow Guest Uploads</p>
            <p className="toggle-description">Let guests share photos from the event</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={allowGuestUploads}
              onChange={(e) => setAllowGuestUploads(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        
        <div className="toggle-row">
          <div className="toggle-info">
            <p className="toggle-label">Allow Comments</p>
            <p className="toggle-description">Enable comments on photos</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={allowGuestComments}
              onChange={(e) => setAllowGuestComments(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Custom Fields */}
      <div className="section-card">
        <label className="section-title">Additional Information</label>
        <p className="section-description">
          Add custom fields like dress code, parking info, or website links
        </p>
        
        {customFields.map((field) => (
          <div key={field.id} className="custom-field-item">
            <div className="field-content">
              <Icon name={field.icon || 'create-outline'} />
              <div className="field-text">
                <p className="field-label">{field.label}</p>
                <p className="field-value">{field.value}</p>
              </div>
            </div>
            <div className="field-actions">
              <button
                type="button"
                className="icon-button"
                onClick={() => handleEditCustomField(field)}
              >
                <Icon name="create" />
              </button>
              <button
                type="button"
                className="icon-button"
                onClick={() => handleDeleteCustomField(field.id)}
              >
                <Icon name="trash" />
              </button>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          className="add-button"
          onClick={handleAddCustomField}
        >
          <Icon name="add-circle" />
          <span>Add Custom Field</span>
        </button>
      </div>

      {/* The modal that was missing.
          Every handler for this existed; nothing rendered the thing they
          controlled, so "Add Custom Field" set a flag and stopped. */}
      {showCustomFieldModal && (
        <CustomFieldModal
          field={currentCustomField}
          onSave={handleSaveCustomField}
          onClose={() => { setShowCustomFieldModal(false); setCurrentCustomField(null); }}
        />
      )}

      {/* Polls.
          `polls` and `setPolls` were passed all the way down here and never
          rendered, so the array stayed empty and the column stayed null. */}
      <PollsSection polls={polls} setPolls={setPolls} eventType={eventType} />

      {/* Music.
          Two bare URL boxes used to live here. They saved the link and the
          invitation never played it, because the invitation page gates
          playback on `selected_music_service` and nothing on this site set it.
          MusicSection makes the service a choice, which is what turns a pasted
          link into music a guest can hear. */}
      <MusicSection
        selectedMusicService={selectedMusicService}
        setSelectedMusicService={setSelectedMusicService}
        playlist={playlist}
        setPlaylist={setPlaylist}
        youtubePlaylist={youtubePlaylist}
        setYoutubePlaylist={setYoutubePlaylist}
      />
    </div>
  );
}